import React, { useState, Component } from 'react';
import { motion } from 'framer-motion';
import { LayoutTemplateIcon, ClockIcon, SparklesIcon, ArrowRightIcon } from 'lucide-react';
const templates = [{
  name: 'Car Loan',
  url: 'https://app.fluid-form.ai/templates/org_38dbPMpQEgbwJMUEGbUS2WVYhnz/car-loan'
}, {
  name: 'Event Registration',
  url: 'https://app.fluid-form.ai/templates/org_38dbPMpQEgbwJMUEGbUS2WVYhnz/event-registration-form'
}, {
  name: 'Customer Feedback',
  url: 'https://app.fluid-form.ai/templates/org_38dbPMpQEgbwJMUEGbUS2WVYhnz/customer-feedback-form'
}, {
  name: 'Lead Capture',
  url: 'https://app.fluid-form.ai/templates/org_38dbPMpQEgbwJMUEGbUS2WVYhnz/lead-capture-form'
}, {
  name: 'Onboarding',
  url: 'https://app.fluid-form.ai/templates/org_38dbPMpQEgbwJMUEGbUS2WVYhnz/onboarding-form'
}, {
  name: 'Pre-Order',
  url: 'https://app.fluid-form.ai/templates/org_38dbPMpQEgbwJMUEGbUS2WVYhnz/pre-order-form'
}];
const Hero = () => {
  const [prompt, setPrompt] = useState('');
  const [isFocused, setIsFocused] = useState(false);
  const isActive = isFocused || prompt.length > 0;
  const handleGenerate = () => {
    const trimmed = prompt.trim();
    if (!trimmed) return;
    window.open(`https://app.fluid-form.ai/forms?p=${encodeURIComponent(trimmed)}`, '_blank', 'noopener,noreferrer');
  };
  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleGenerate();
    }
  };
  return <section className="relative w-full bg-gradient-to-b from-white via-orange-50/30 to-white pt-[4.75rem] pb-10 overflow-hidden" data-id="element-175">
      <div className="container mx-auto px-4 relative z-10" data-id="element-176">
        <div className="max-w-4xl mx-auto text-center" data-id="element-177">
          {/* Animated Badge */}
          <motion.div className="inline-flex items-center gap-2 bg-orange-50 border border-orange-200/60 rounded-full px-3 py-1.5 mb-4 sm:mb-6 md:mb-8" initial={{
          opacity: 0,
          y: 12,
          scale: 0.95
        }} animate={{
          opacity: 1,
          y: 0,
          scale: 1
        }} transition={{
          duration: 0.6,
          ease: 'easeOut'
        }} data-id="element-178">
            <ClockIcon size={14} className="text-orange-500" data-id="element-179" />
            <span className="text-xs sm:text-sm font-medium text-orange-700 tracking-tight" data-id="element-180">
              Build in minutes, not days
            </span>
          </motion.div>

          <motion.h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl xl:text-7xl font-bold text-gray-900 leading-tight mb-4 sm:mb-6 md:mb-8 tracking-tight px-2" initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.8,
          ease: 'easeOut'
        }} data-id="element-181">
            AI-native Forms —{' '}
            <span className="text-orange-500" data-id="element-182">that feel human.</span>
          </motion.h1>

          <motion.p className="text-base sm:text-lg md:text-xl lg:text-2xl text-gray-600 mb-6 sm:mb-8 md:mb-10 max-w-3xl mx-auto leading-relaxed px-2" initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.8,
          delay: 0.1,
          ease: 'easeOut'
        }} data-id="element-183">
            Let users respond naturally with text, voice, documents, and photos
            — and receive clean, validated, schema-aligned data every time.
          </motion.p>

          {/* Form Builder Card */}
          <motion.div className="max-w-3xl mx-auto" initial={{
          opacity: 0,
          y: 30
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          delay: 0.3
        }} data-id="element-184">
            <div className="relative" data-id="element-185">
              {/* Animated outer glow */}
              <motion.div className="absolute -inset-3 rounded-3xl" animate={isActive ? {
              background: 'radial-gradient(ellipse at center, rgba(251,146,60,0.15) 0%, rgba(251,146,60,0.05) 50%, transparent 70%)',
              scale: 1.02
            } : {
              background: 'radial-gradient(ellipse at center, rgba(251,146,60,0.08) 0%, rgba(251,146,60,0.02) 50%, transparent 70%)',
              scale: 1
            }} transition={{
              duration: 0.5,
              ease: 'easeOut'
            }} data-id="element-186" />

              {/* Pulsing ring on hover/focus */}
              <motion.div className="absolute -inset-[2px] rounded-2xl border-2 border-orange-300/0 pointer-events-none" animate={isActive ? {
              borderColor: 'rgba(251,146,60,0.3)',
              opacity: [0.5, 1, 0.5]
            } : {
              borderColor: 'rgba(251,146,60,0)',
              opacity: 1
            }} transition={isActive ? {
              borderColor: {
                duration: 0.3
              },
              opacity: {
                duration: 2,
                repeat: Infinity,
                ease: 'easeInOut'
              }
            } : {
              duration: 0.3
            }} data-id="element-187" />

              <motion.div className="relative bg-white rounded-2xl shadow-xl border border-gray-200 overflow-hidden" animate={isActive ? {
              boxShadow: '0 20px 60px -12px rgba(251,146,60,0.2), 0 8px 20px -8px rgba(0,0,0,0.08)',
              borderColor: '#fdba74'
            } : {
              boxShadow: '0 10px 30px -8px rgba(0,0,0,0.08), 0 4px 10px -4px rgba(0,0,0,0.04)',
              borderColor: '#e5e7eb'
            }} whileHover={!isActive ? {
              boxShadow: '0 16px 48px -10px rgba(251,146,60,0.12), 0 6px 16px -6px rgba(0,0,0,0.06)',
              borderColor: '#fed7aa'
            } : {}} transition={{
              duration: 0.4,
              ease: 'easeOut'
            }} data-id="element-188">
                {/* Prompt Input Area */}
                <div className="p-4 sm:p-6 md:p-8" data-id="element-189">
                  <div className="flex items-center gap-2 sm:gap-2.5 mb-4 sm:mb-5" data-id="element-190">
                    <motion.div className="bg-orange-100 p-1.5 sm:p-2 rounded-lg" animate={isActive ? {
                    rotate: [0, 15, -10, 5, 0],
                    scale: [1, 1.15, 1]
                  } : {}} transition={{
                    duration: 0.6,
                    ease: 'easeOut'
                  }} data-id="element-191">
                      <SparklesIcon size={18} className="text-orange-500 sm:w-5 sm:h-5" data-id="element-192" />
                    </motion.div>
                    <span className="text-base sm:text-lg font-bold text-gray-900" data-id="element-193">
                      Create your first form with AI
                    </span>
                  </div>

                  <div className="w-full" data-id="element-194">
                    <motion.div animate={isFocused ? {
                    boxShadow: '0 0 0 4px rgba(249, 115, 22, 0.1)',
                    borderColor: '#fb923c'
                  } : {
                    boxShadow: '0 0 0 0px rgba(249, 115, 22, 0)',
                    borderColor: '#e5e7eb'
                  }} transition={{
                    duration: 0.25
                  }} className="w-full rounded-xl border bg-white overflow-hidden relative" data-id="element-195">
                      <textarea value={prompt} onChange={e => setPrompt(e.target.value)} onKeyDown={e => {
                      if (e.key === 'Enter' && !e.shiftKey) {
                        e.preventDefault();
                        handleGenerate();
                      }
                    }} onFocus={() => setIsFocused(true)} onBlur={() => setIsFocused(false)} placeholder="Describe your form (e.g., create a job application form)" rows={2} className="w-full px-3 sm:px-4 pt-3 sm:pt-3.5 pb-12 sm:pb-14 text-sm sm:text-base text-gray-900 placeholder-gray-400 focus:outline-none resize-none" data-id="element-196" />
                      <div className="absolute bottom-2 sm:bottom-3 right-2 sm:right-3" data-id="element-197">
                        <motion.button onClick={handleGenerate} disabled={!prompt.trim()} className="inline-flex items-center justify-center gap-1.5 sm:gap-2 bg-orange-500 text-white pl-3 sm:pl-4 pr-2.5 sm:pr-3.5 py-2 sm:py-2.5 rounded-lg text-xs sm:text-sm font-semibold hover:bg-orange-600 transition-all shadow-sm hover:shadow-md disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:bg-orange-500 disabled:hover:shadow-sm min-h-[44px]" whileHover={prompt.trim() ? {
                        scale: 1.03
                      } : {}} whileTap={prompt.trim() ? {
                        scale: 0.97
                      } : {}} animate={prompt.trim() ? {
                        opacity: 1
                      } : {
                        opacity: 0.5
                      }} transition={{
                        duration: 0.2
                      }} data-id="element-198">
                          <SparklesIcon size={14} className="sm:w-[15px] sm:h-[15px]" data-id="element-199" />
                          <span className="hidden xs:inline" data-id="element-200">
                            Generate Form
                          </span>
                          <span className="xs:hidden" data-id="element-201">Generate</span>
                          <ArrowRightIcon size={14} className="sm:w-[15px] sm:h-[15px]" data-id="element-202" />
                        </motion.button>
                      </div>
                    </motion.div>
                  </div>
                </div>

                {/* Divider + Template chips */}
                <div className="bg-gray-50/50 px-4 sm:px-6 md:px-8 py-4 sm:py-5 border-t border-gray-100" data-id="element-203">
                  <div className="flex flex-col sm:flex-row items-start sm:items-center gap-2 sm:gap-3 md:gap-4" data-id="element-204">
                    <div className="flex items-center gap-2 text-xs sm:text-sm text-gray-500 whitespace-nowrap" data-id="element-205">
                      <LayoutTemplateIcon size={14} className="sm:w-4 sm:h-4" data-id="element-206" />
                      <span data-id="element-207">Or start with a template:</span>
                    </div>
                    <div className="flex flex-wrap gap-2" data-id="element-208">
                      {templates.map((template, index) => <motion.a key={template.name} href={template.url} target="_blank" rel="noopener noreferrer" className="px-3 sm:px-4 py-1.5 sm:py-2 bg-orange-50 hover:bg-orange-100 border border-orange-200 hover:border-orange-300 rounded-full text-xs sm:text-sm font-semibold text-orange-700 hover:text-orange-800 transition-all shadow-sm hover:shadow min-h-[44px] flex items-center" initial={{
                      opacity: 0,
                      y: 8
                    }} animate={{
                      opacity: 1,
                      y: 0
                    }} transition={{
                      delay: 0.5 + index * 0.07,
                      duration: 0.35,
                      ease: 'easeOut'
                    }} whileHover={{
                      scale: 1.06,
                      y: -1
                    }} whileTap={{
                      scale: 0.97
                    }} data-id="element-209">
                          {template.name}
                        </motion.a>)}
                    </div>
                  </div>
                </div>
              </motion.div>
            </div>

            <p className="text-xs sm:text-sm text-gray-400 mt-3 sm:mt-4" data-id="element-210">
              No credit card required • Free to start
            </p>
          </motion.div>
        </div>
      </div>
    </section>;
};
export default Hero;